import React, {
    useEffect,
    useRef,
    useState
}
from "react";

import {
    OMNISCAN_BLUE
}
from "../../../constants/colors";

import {
    functions
}
from "../../../constants/common-functions";


//========================================================
// Document Cell
//
// Universal document content editor.
//
// Supports:
// - Dragging source content from the basket
// - Creating authored text
// - Editing a copy of source text
// - Basic rich-text formatting
// - Uploading and previewing a local image
// - Editing an image caption
//
// Source basket items are NEVER modified.
//
// Cell content is passed to the parent through
// onUpdateCellContent when that callback is connected.
//
// Uploaded File objects remain in React memory until
// the document creation/upload route is implemented.
//========================================================

export default function DocumentCell({

    pageId,
    rowId,
    cell,
    basketItems = [],
    onSetCellContent,
    onClearCellContent,
    onUpdateCellContent

})
{

    //========================================================
    // State
    //========================================================

    const [dragOver, setDragOver] =
        useState(false);

    const [createMenuOpen, setCreateMenuOpen] =
        useState(false);

    const [localContent, setLocalContent] =
        useState(null);

    const [imageUrl, setImageUrl] =
        useState(null);

    const [imageLoading, setImageLoading] =
        useState(false);

    const [imageError, setImageError] =
        useState(false);

    const [editorVersion, setEditorVersion] =
        useState(0);


    //========================================================
    // Refs
    //========================================================

    const editorRef =
        useRef(null);

    const uploadRef =
        useRef(null);

    const editorSelectionRef =
        useRef(null);

    const previousContentIdentityRef =
        useRef(null);

    const localContentRef =
        useRef(null);


    //========================================================
    // Content
    //========================================================

    const parentContent =
        cell?.content || null;

    const content =
        localContent !== null
            ? localContent
            : parentContent;


    //========================================================
    // Basket Item
    //
    // Use the effective content so that source attribution
    // continues working while a source copy is being edited.
    //========================================================

    const basketItem =
        basketItems.find(

            item =>
                String(item.basketId) ===
                String(content?.basketId)

        );


    //========================================================
    // Content Types
    //========================================================

    const isSourceImage =
        basketItem?.basketContentType ===
        "IMAGE_SECTION";

    const isDerivedImage =
        basketItem?.basketContentType ===
        "DERIVED_IMAGE";

    const isSourceText =
        basketItem?.basketContentType ===
        "TEXT_SECTION";

    const isAuthoredText =
        content?.contentType ===
        "TEXT";

    const isUploadedImage =
        content?.contentType ===
        "UPLOADED_IMAGE";

    const isText =
        isSourceText ||
        isAuthoredText;

    const isImage =
        isSourceImage ||
        isDerivedImage ||
        isUploadedImage;

    const hasContent =
        Boolean(
            content ||
            basketItem
        );


    //========================================================
    // Content Label
    //========================================================

    const contentLabel =
        isUploadedImage
            ? "Uploaded Image"
            : isDerivedImage
                ? "Composed Image"
                : isSourceImage
                    ? "Source Image"
                    : isSourceText
                        ? "Source Text"
                        : isAuthoredText
                            ? "Document Text"
                            : "Source Content";


    //========================================================
    // Content Identity
    //
    // Do not reset local editing state when the parent
    // simply echoes an edit back into the layout.
    //
    // A new basket item or uploaded image represents
    // genuinely different cell content.
    //========================================================

    const contentIdentity =
        content?.basketId != null
            ? "basket:" + content.basketId
            : content?.uploadKey
                ? "upload:" + content.uploadKey
                : content?.contentType
                    ? "type:" + content.contentType
                    : "empty";


    //========================================================
    // Synchronise External Content Changes
    //========================================================

    useEffect(

        () =>
        {

            const previousIdentity =
                previousContentIdentityRef.current;

            previousContentIdentityRef.current =
                contentIdentity;

            if (
                previousIdentity === null
            )
            {
                return;
            }

            if (
                previousIdentity !== contentIdentity
            )
            {

                localContentRef.current =
                    null;

                setLocalContent(
                    null
                );

                setCreateMenuOpen(
                    false
                );

                editorSelectionRef.current =
                    null;

                setEditorVersion(
                    version => version + 1
                );

            }

        },

        [
            contentIdentity
        ]

    );


    //========================================================
    // Image File
    //========================================================

    const uploadedImageFile =
        isUploadedImage
            ? content?.imageFile
            : null;


    //========================================================
    // Load Image
    //
    // Every object URL belongs to this effect and is
    // revoked when the image changes or the cell unmounts.
    //
    // Uploaded image previews can therefore be recreated
    // after remounting from the File held by the parent.
    //========================================================

    useEffect(

        () =>
        {

            let cancelled =
                false;

            let objectUrl =
                null;

            setImageUrl(
                null
            );

            setImageLoading(
                false
            );

            setImageError(
                false
            );


            //================================================
            // Uploaded Image
            //================================================

            if (
                isUploadedImage
            )
            {

                if (
                    !uploadedImageFile
                )
                {

                    setImageError(
                        true
                    );

                    return undefined;

                }

                try
                {

                    objectUrl =
                        window.URL.createObjectURL(
                            uploadedImageFile
                        );

                    setImageUrl(
                        objectUrl
                    );

                }
                catch (error)
                {

                    console.error(
                        "Unable to display uploaded image.",
                        error
                    );

                    setImageError(
                        true
                    );

                }

                return () =>
                {

                    cancelled =
                        true;

                    if (
                        objectUrl
                    )
                    {

                        window.URL.revokeObjectURL(
                            objectUrl
                        );

                    }

                };

            }


            //================================================
            // Derived Image
            //================================================

            if (
                isDerivedImage
            )
            {

                if (
                    !basketItem?.blob
                )
                {

                    setImageError(
                        true
                    );

                    return undefined;

                }

                try
                {

                    objectUrl =
                        window.URL.createObjectURL(
                            basketItem.blob
                        );

                    if (
                        !cancelled
                    )
                    {

                        setImageUrl(
                            objectUrl
                        );

                    }

                }
                catch (error)
                {

                    console.error(
                        "Unable to display derived image.",
                        error
                    );

                    setImageError(
                        true
                    );

                }

                return () =>
                {

                    cancelled =
                        true;

                    if (
                        objectUrl
                    )
                    {

                        window.URL.revokeObjectURL(
                            objectUrl
                        );

                    }

                };

            }


            //================================================
            // Source Image
            //================================================

            if (
                !isSourceImage
            )
            {
                return undefined;
            }

            if (
                !basketItem?.documentSnippetSectionId
            )
            {

                setImageError(
                    true
                );

                return undefined;

            }

            setImageLoading(
                true
            );

            const url =
                "/resolutionsoftwareplatform/snippet-sniffer/sections/" +
                basketItem.documentSnippetSectionId +
                "/image";

            functions.getBlobFromAjax(

                url,

                function(blob)
                {

                    if (
                        cancelled
                    )
                    {
                        return;
                    }

                    if (
                        !blob
                    )
                    {

                        setImageLoading(
                            false
                        );

                        setImageError(
                            true
                        );

                        return;

                    }

                    try
                    {

                        objectUrl =
                            window.URL.createObjectURL(
                                blob
                            );

                        setImageUrl(
                            objectUrl
                        );

                        setImageLoading(
                            false
                        );

                    }
                    catch (error)
                    {

                        console.error(
                            "Unable to display source image.",
                            error
                        );

                        setImageLoading(
                            false
                        );

                        setImageError(
                            true
                        );

                    }

                }

            );

            return () =>
            {

                cancelled =
                    true;

                if (
                    objectUrl
                )
                {

                    window.URL.revokeObjectURL(
                        objectUrl
                    );

                }

            };

        },

        [
            isUploadedImage,
            isSourceImage,
            isDerivedImage,
            uploadedImageFile,
            basketItem?.basketId,
            basketItem?.documentSnippetSectionId,
            basketItem?.blob
        ]

    );


    //========================================================
    // Commit Content
    //
    // Keep the local draft immediately responsive.
    //
    // Also update the parent layout when the callback
    // has been connected through the component hierarchy.
    //========================================================

    const commitContent = (updatedContent) =>
    {

        localContentRef.current =
            updatedContent;

        setLocalContent(
            updatedContent
        );

        if (
            onUpdateCellContent
        )
        {

            onUpdateCellContent(

                pageId,
                rowId,
                cell.id,
                updatedContent

            );

        }

    };


    //========================================================
    // Update Content
    //========================================================

    const updateContent = (changes) =>
    {

        const currentContent =
            localContentRef.current ||
            content ||
            {};

        const updatedContent =
        {

            ...currentContent,
            ...changes

        };

        commitContent(
            updatedContent
        );

    };


    //========================================================
    // Create Text
    //========================================================

    const handleCreateText = (event) =>
    {

        event.preventDefault();
        event.stopPropagation();

        const newContent =
        {

            contentType:
                "TEXT",

            basketId:
                null,

            title:
                "",

            text:
                "",

            html:
                "",

            formatting:
            {

                alignment:
                    "left"

            }

        };

        commitContent(
            newContent
        );

        setCreateMenuOpen(
            false
        );

        setEditorVersion(
            version => version + 1
        );

    };


    //========================================================
    // Upload Image
    //========================================================

    const handleUploadImage = (event) =>
    {

        event.preventDefault();
        event.stopPropagation();

        setCreateMenuOpen(
            false
        );

        if (
            uploadRef.current
        )
        {

            uploadRef.current.click();

        }

    };


    //========================================================
    // Image Selected
    //
    // Retain the File in the parent React layout.
    //
    // The File must be excluded from JSON and appended
    // separately to FormData during document creation.
    //========================================================

    const handleImageSelected = (event) =>
    {

        const file =
            event.target.files?.[0];

        if (
            !file
        )
        {
            return;
        }

        if (
            !file.type.startsWith("image/")
        )
        {

            event.target.value =
                "";

            return;

        }

        const uploadKey =
            "upload-" +
            Date.now() +
            "-" +
            Math.random().toString(36).slice(2);

        const newContent =
        {

            contentType:
                "UPLOADED_IMAGE",

            basketId:
                null,

            uploadKey:
                uploadKey,

            imageFile:
                file,

            fileName:
                file.name,

            caption:
                "",

            formatting:
            {

                alignment:
                    "center"

            }

        };

        commitContent(
            newContent
        );

        setCreateMenuOpen(
            false
        );

        // Allow the same file to be selected again.

        event.target.value =
            "";

    };


    //========================================================
    // Initial Editor Content
    //
    // Prefer the edited snapshot over original source text.
    // The source basket item remains unchanged.
    //========================================================

    const initialText =
        content?.text !== undefined
            ? content.text
            : basketItem?.selectedText || "";


    //========================================================
    // Populate Rich Text Editor
    //
    // Only initialise when content identity changes
    // or when a newly created editor appears.
    //
    // Do not repopulate on every keystroke because that
    // would reset the caret and text selection.
    //========================================================

    useEffect(

        () =>
        {

            if (
                !editorRef.current ||
                !isText
            )
            {
                return;
            }

            const editor =
                editorRef.current;

            const currentContent =
                localContentRef.current ||
                content;

            if (
                currentContent?.html
            )
            {

                editor.innerHTML =
                    currentContent.html;

            }
            else
            {

                editor.textContent =
                    currentContent?.text !== undefined
                        ? currentContent.text
                        : initialText;

            }

        },

        [
            editorVersion,
            contentIdentity,
            isText
        ]

    );


    //========================================================
    // Editor Change
    //========================================================

    const handleEditorInput = () =>
    {

        if (
            !editorRef.current
        )
        {
            return;
        }

        updateContent(

            {

                contentType:
                    "TEXT",

                basketId:
                    content?.basketId ??
                    basketItem?.basketId ??
                    null,

                text:
                    editorRef.current.innerText,

                html:
                    editorRef.current.innerHTML

            }

        );

    };


    //========================================================
    // Remember Selection
    //========================================================

    const rememberSelection = () =>
    {

        const selection =
            window.getSelection();

        if (

            selection &&
            selection.rangeCount > 0 &&
            editorRef.current?.contains(
                selection.anchorNode
            ) &&
            editorRef.current?.contains(
                selection.focusNode
            )

        )
        {

            editorSelectionRef.current =
                selection.getRangeAt(0).cloneRange();

        }

    };


    //========================================================
    // Restore Selection
    //========================================================

    const restoreSelection = () =>
    {

        const selection =
            window.getSelection();

        if (

            !selection ||
            !editorSelectionRef.current

        )
        {
            return;
        }

        selection.removeAllRanges();

        selection.addRange(
            editorSelectionRef.current
        );

    };


    //========================================================
    // Format Text
    //
    // Retains the existing browser editing commands.
    //========================================================

    const handleFormat = (
        event,
        command,
        value = null
    ) =>
    {

        event.preventDefault();
        event.stopPropagation();

        if (
            !editorRef.current
        )
        {
            return;
        }

        editorRef.current.focus();

        restoreSelection();

        document.execCommand(

            command,
            false,
            value

        );

        handleEditorInput();

        rememberSelection();

    };


    //========================================================
    // Clear Content
    //========================================================

    const handleRemoveContent = (event) =>
    {

        event.preventDefault();
        event.stopPropagation();

        localContentRef.current =
            null;

        setLocalContent(
            null
        );

        setImageUrl(
            null
        );

        setCreateMenuOpen(
            false
        );

        editorSelectionRef.current =
            null;

        if (
            onClearCellContent
        )
        {

            onClearCellContent(

                pageId,
                rowId,
                cell.id

            );

        }
        else if (
            onUpdateCellContent
        )
        {

            onUpdateCellContent(

                pageId,
                rowId,
                cell.id,
                null

            );

        }

        setEditorVersion(
            version => version + 1
        );

    };


    //========================================================
    // Drag Over
    //
    // Visual state only.
    //========================================================

    const handleDragOver = (event) =>
    {

        event.preventDefault();

        event.dataTransfer.dropEffect =
            "copy";

        setDragOver(
            true
        );

    };


    //========================================================
    // Drag Enter
    //========================================================

    const handleDragEnter = (event) =>
    {

        event.preventDefault();

        setDragOver(
            true
        );

    };


    //========================================================
    // Drag Leave
    //========================================================

    const handleDragLeave = (event) =>
    {

        event.preventDefault();

        if (

            event.relatedTarget &&
            event.currentTarget.contains(
                event.relatedTarget
            )

        )
        {
            return;
        }

        setDragOver(
            false
        );

    };


    //========================================================
    // Drop
    //
    // Only an actual valid basket drop changes content.
    //========================================================

    const handleDrop = (event) =>
    {

        event.preventDefault();
        event.stopPropagation();

        setDragOver(
            false
        );

        const basketId =

            event.dataTransfer.getData(
                "application/x-omniscan-basket-item"
            )

            ||

            event.dataTransfer.getData(
                "text/plain"
            );

        if (

            !basketId ||
            !onSetCellContent

        )
        {
            return;
        }

        const matchingItem =
            basketItems.find(

                item =>
                    String(item.basketId) ===
                    String(basketId)

            );

        if (
            !matchingItem
        )
        {
            return;
        }

        localContentRef.current =
            null;

        setLocalContent(
            null
        );

        setCreateMenuOpen(
            false
        );

        editorSelectionRef.current =
            null;

        onSetCellContent(

            pageId,
            rowId,
            cell.id,
            matchingItem.basketId

        );

        setEditorVersion(
            version => version + 1
        );

    };


    //========================================================
    // Width
    //========================================================

    const cellStyle =
    {

        ...styles.cell,

        ...(hasContent
            ? styles.cellPopulated
            : {}),

        ...(dragOver
            ? styles.cellDragOver
            : {}),

        flexGrow:
            cell.width,

        flexShrink:
            1,

        flexBasis:
            0

    };


    //========================================================
    // Render
    //========================================================

    return (

        <div

            style={cellStyle}

            onDragOver={handleDragOver}

            onDragEnter={handleDragEnter}

            onDragLeave={handleDragLeave}

            onDrop={handleDrop}

        >

            {/*============================================
                Hidden Image Input
            ============================================*/}

            <input

                ref={uploadRef}

                type="file"

                accept="image/*"

                style={styles.hiddenInput}

                onChange={handleImageSelected}

            />


            {/*============================================
                Empty Cell
            ============================================*/}

            {
                !hasContent &&
                (

                    <div style={styles.empty}>

                        <button

                            type="button"

                            style={styles.createButton}

                            onClick={
                                (event) =>
                                {

                                    event.stopPropagation();

                                    setCreateMenuOpen(
                                        !createMenuOpen
                                    );

                                }
                            }

                            title="Create document content"

                        >

                            +

                        </button>


                        <div style={styles.emptyTitle}>

                            Create or Add Content

                        </div>


                        <div style={styles.emptySubtitle}>

                            Start writing or drag from your basket

                        </div>


                        {
                            createMenuOpen &&
                            (

                                <div style={styles.createMenu}>

                                    <button

                                        type="button"

                                        style={styles.createMenuButton}

                                        onClick={handleCreateText}

                                    >

                                        <span style={styles.menuIcon}>

                                            T

                                        </span>

                                        <span>

                                            Add Text

                                        </span>

                                    </button>


                                    <button

                                        type="button"

                                        style={styles.createMenuButton}

                                        onClick={handleUploadImage}

                                    >

                                        <span style={styles.menuIcon}>

                                            ▧

                                        </span>

                                        <span>

                                            Upload Image

                                        </span>

                                    </button>

                                </div>

                            )
                        }


                        <div style={styles.width}>

                            {
                                Math.round(
                                    cell.width
                                )
                            }% width

                        </div>

                    </div>

                )
            }


            {/*============================================
                Populated Cell Header
            ============================================*/}

            {
                hasContent &&
                (

                    <div style={styles.cellHeader}>

                        <span style={styles.contentLabel}>

                            {contentLabel}

                        </span>


                        <button

                            type="button"

                            style={styles.removeButton}

                            onClick={handleRemoveContent}

                            title="Remove content"

                            aria-label="Remove content"

                        >

                            ×

                        </button>

                    </div>

                )
            }


            {/*============================================
                Text Editor
            ============================================*/}

            {
                hasContent &&
                isText &&
                (

                    <div style={styles.editorContainer}>

                        {/*================================
                            Formatting Toolbar
                        =================================*/}

                        <div style={styles.formatToolbar}>

                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Bold"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "bold"
                                        )
                                }

                            >

                                <strong>B</strong>

                            </button>


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Italic"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "italic"
                                        )
                                }

                            >

                                <em>I</em>

                            </button>


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Underline"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "underline"
                                        )
                                }

                            >

                                <u>U</u>

                            </button>


                            <span style={styles.toolbarDivider} />


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Heading"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "formatBlock",
                                            "h2"
                                        )
                                }

                            >

                                H

                            </button>


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Paragraph"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "formatBlock",
                                            "p"
                                        )
                                }

                            >

                                ¶

                            </button>


                            <span style={styles.toolbarDivider} />


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Bullet List"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "insertUnorderedList"
                                        )
                                }

                            >

                                • ≡

                            </button>


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Numbered List"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "insertOrderedList"
                                        )
                                }

                            >

                                1 ≡

                            </button>


                            <span style={styles.toolbarDivider} />


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Align Left"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "justifyLeft"
                                        )
                                }

                            >

                                ≡

                            </button>


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Align Center"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "justifyCenter"
                                        )
                                }

                            >

                                ☰

                            </button>


                            <button

                                type="button"

                                style={styles.formatButton}

                                title="Align Right"

                                onMouseDown={
                                    event =>
                                        handleFormat(
                                            event,
                                            "justifyRight"
                                        )
                                }

                            >

                                ≡

                            </button>

                        </div>


                        {/*================================
                            Title
                        =================================*/}

                        <input

                            type="text"

                            value={content?.title || ""}

                            placeholder="Add a title..."

                            style={styles.titleInput}

                            onChange={
                                event =>
                                    updateContent(
                                        {

                                            contentType:
                                                "TEXT",

                                            basketId:
                                                content?.basketId ??
                                                basketItem?.basketId ??
                                                null,

                                            title:
                                                event.target.value

                                        }
                                    )
                            }

                        />


                        {/*================================
                            Editable Content
                        =================================*/}

                        <div

                            ref={editorRef}

                            contentEditable

                            suppressContentEditableWarning

                            role="textbox"

                            aria-label="Document text"

                            aria-multiline="true"

                            style={styles.richTextEditor}

                            onInput={handleEditorInput}

                            onKeyUp={rememberSelection}

                            onMouseUp={rememberSelection}

                            onBlur={rememberSelection}

                        />


                        {/*================================
                            Source Attribution
                        =================================*/}

                        {
                            isSourceText &&
                            (

                                <div style={styles.sourceFooter}>

                                    <span style={styles.sourceDot} />

                                    Source linked · Editing a copy

                                </div>

                            )
                        }

                    </div>

                )
            }


            {/*============================================
                Image Content
            ============================================*/}

            {
                hasContent &&
                isImage &&
                (

                    <div style={styles.imageContainer}>

                        {
                            imageLoading &&
                            (

                                <div style={styles.imageMessage}>

                                    Loading image...

                                </div>

                            )
                        }


                        {
                            imageError &&
                            (

                                <div style={styles.imageMessage}>

                                    Unable to load image

                                </div>

                            )
                        }


                        {
                            imageUrl &&
                            (

                                <img

                                    src={imageUrl}

                                    alt={
                                        content?.caption ||
                                        "Document image"
                                    }

                                    style={styles.image}

                                    draggable={false}

                                />

                            )
                        }


                        {/*================================
                            Image Caption
                        =================================*/}

                        <input

                            type="text"

                            value={content?.caption || ""}

                            placeholder="Add an image caption..."

                            style={styles.captionInput}

                            onChange={
                                event =>
                                    updateContent(
                                        {

                                            caption:
                                                event.target.value

                                        }
                                    )
                            }

                        />


                        {
                            isUploadedImage &&
                            (

                                <div style={styles.localDraftLabel}>

                                    Local image preview · Not uploaded yet

                                </div>

                            )
                        }

                    </div>

                )
            }


            {/*============================================
                Unknown Content
            ============================================*/}

            {
                hasContent &&
                !isText &&
                !isImage &&
                (

                    <div style={styles.unknownContent}>

                        Source content

                    </div>

                )
            }


            {/*============================================
                Drop Overlay
            ============================================*/}

            {
                dragOver &&
                (

                    <div

                        style={
                            {

                                ...styles.dropOverlay,

                                ...(hasContent
                                    ? styles.dropOverlayPopulated
                                    : styles.dropOverlayEmpty)

                            }
                        }

                    >

                        <div style={styles.dropTargetMessage}>

                            <div style={styles.dropIcon}>

                                +

                            </div>


                            <div style={styles.dropText}>

                                {
                                    hasContent
                                        ? "Replace Content"
                                        : "Drop Here"
                                }

                            </div>

                        </div>

                    </div>

                )
            }

        </div>

    );

}


//========================================================
// Styles
//========================================================

const styles =
{

    //========================================================
    // Cell
    //========================================================

    cell:
    {

        position: "relative",

        minWidth: 0,

        minHeight: 180,

        display: "flex",

        flexDirection: "column",

        border: "1px dashed rgba(38,55,70,0.18)",

        borderRadius: 5,

        background: "#fafcfd",

        boxSizing: "border-box",

        overflow: "hidden",

        transition:
            "border-color 160ms ease, background 160ms ease, box-shadow 160ms ease"

    },


    cellPopulated:
    {

        border: "1px solid rgba(38,55,70,0.12)",

        background: "#ffffff"

    },


    cellDragOver:
    {

        border: "1px solid " + OMNISCAN_BLUE,

        boxShadow:
            "inset 0 0 0 2px rgba(32,175,232,0.10)"

    },


    //========================================================
    // Hidden Input
    //========================================================

    hiddenInput:
    {

        display: "none"

    },


    //========================================================
    // Empty Cell
    //========================================================

    empty:
    {

        flex: 1,

        minHeight: 180,

        padding: 18,

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        justifyContent: "center",

        boxSizing: "border-box",

        textAlign: "center"

    },


    createButton:
    {

        width: 40,

        height: 40,

        marginBottom: 12,

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        border: "1px solid rgba(32,175,232,0.30)",

        borderRadius: 7,

        background: "rgba(32,175,232,0.06)",

        color: OMNISCAN_BLUE,

        fontSize: 26,

        fontWeight: 300,

        cursor: "pointer"

    },


    emptyTitle:
    {

        color: "#40566a",

        fontSize: 11,

        fontWeight: 700

    },


    emptySubtitle:
    {

        marginTop: 5,

        color: "#8b99a6",

        fontSize: 9

    },


    width:
    {

        marginTop: 14,

        color: "#a2adb7",

        fontSize: 8,

        fontWeight: 600

    },


    //========================================================
    // Create Menu
    //========================================================

    createMenu:
    {

        width: "100%",

        maxWidth: 210,

        marginTop: 15,

        padding: 5,

        display: "flex",

        flexDirection: "column",

        gap: 4,

        border: "1px solid rgba(38,55,70,0.10)",

        borderRadius: 6,

        background: "#ffffff",

        boxShadow:
            "0 6px 18px rgba(20,35,50,0.08)",

        boxSizing: "border-box"

    },


    createMenuButton:
    {

        width: "100%",

        minHeight: 34,

        padding: "7px 10px",

        display: "flex",

        alignItems: "center",

        gap: 10,

        border: "none",

        borderRadius: 4,

        background: "#f7f9fb",

        color: "#263746",

        fontSize: 10,

        fontWeight: 600,

        textAlign: "left",

        cursor: "pointer"

    },


    menuIcon:
    {

        width: 23,

        height: 23,

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        borderRadius: 4,

        background: "rgba(32,175,232,0.10)",

        color: OMNISCAN_BLUE,

        fontSize: 13,

        fontWeight: 700

    },


    //========================================================
    // Cell Header
    //========================================================

    cellHeader:
    {

        height: 32,

        minHeight: 32,

        padding: "0 8px 0 12px",

        display: "flex",

        alignItems: "center",

        justifyContent: "space-between",

        borderBottom:
            "1px solid rgba(38,55,70,0.08)",

        background: "#f7f9fb",

        boxSizing: "border-box"

    },


    contentLabel:
    {

        color: "#617586",

        fontSize: 9,

        fontWeight: 700,

        letterSpacing: 0.4,

        textTransform: "uppercase"

    },


    removeButton:
    {

        width: 22,

        height: 22,

        padding: 0,

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        border: "1px solid rgba(166,60,60,0.14)",

        borderRadius: 4,

        background: "#ffffff",

        color: "#9a4a4a",

        fontSize: 16,

        cursor: "pointer"

    },


    //========================================================
    // Text Editor
    //========================================================

    editorContainer:
    {

        width: "100%",

        minWidth: 0,

        display: "flex",

        flexDirection: "column",

        boxSizing: "border-box"

    },


    formatToolbar:
    {

        minHeight: 38,

        padding: "5px 7px",

        display: "flex",

        alignItems: "center",

        flexWrap: "wrap",

        gap: 3,

        borderBottom:
            "1px solid rgba(38,55,70,0.08)",

        background: "#ffffff",

        boxSizing: "border-box"

    },


    formatButton:
    {

        minWidth: 25,

        height: 25,

        padding: "0 5px",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        border: "1px solid rgba(38,55,70,0.10)",

        borderRadius: 3,

        background: "#ffffff",

        color: "#40566a",

        fontSize: 11,

        cursor: "pointer"

    },


    toolbarDivider:
    {

        width: 1,

        height: 18,

        margin: "0 3px",

        background: "rgba(38,55,70,0.12)"

    },


    titleInput:
    {

        width: "100%",

        minWidth: 0,

        padding: "17px 15px 8px",

        border: "none",

        outline: "none",

        background: "#ffffff",

        color: "#263746",

        fontSize: 16,

        fontWeight: 700,

        boxSizing: "border-box"

    },


    richTextEditor:
    {

        width: "100%",

        minHeight: 145,

        padding: "8px 15px 20px",

        border: "none",

        outline: "none",

        background: "#ffffff",

        color: "#263746",

        fontFamily: "inherit",

        fontSize: 11,

        lineHeight: 1.65,

        overflowWrap: "break-word",

        boxSizing: "border-box",

        cursor: "text"

    },


    sourceFooter:
    {

        minHeight: 28,

        padding: "7px 12px",

        display: "flex",

        alignItems: "center",

        gap: 6,

        borderTop:
            "1px solid rgba(38,55,70,0.07)",

        background: "#f7f9fb",

        color: "#758493",

        fontSize: 9,

        fontWeight: 600,

        boxSizing: "border-box"

    },


    sourceDot:
    {

        width: 6,

        height: 6,

        borderRadius: "50%",

        background: "#4aaf80"

    },


    //========================================================
    // Image
    //========================================================

    imageContainer:
    {

        width: "100%",

        minWidth: 0,

        padding: 12,

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        gap: 10,

        boxSizing: "border-box",

        background: "#ffffff"

    },


    image:
    {

        display: "block",

        width: "100%",

        maxWidth: "100%",

        maxHeight: 420,

        objectFit: "contain"

    },


    imageMessage:
    {

        padding: 20,

        color: "#8b99a6",

        fontSize: 10,

        fontWeight: 600

    },


    captionInput:
    {

        width: "100%",

        padding: "8px 10px",

        border: "1px solid rgba(38,55,70,0.10)",

        borderRadius: 4,

        outline: "none",

        background: "#fafcfd",

        color: "#40566a",

        fontSize: 10,

        textAlign: "center",

        boxSizing: "border-box"

    },


    localDraftLabel:
    {

        color: "#9a7b40",

        fontSize: 9,

        fontWeight: 600,

        textAlign: "center"

    },


    unknownContent:
    {

        padding: 20,

        color: "#8b99a6",

        fontSize: 10,

        fontStyle: "italic"

    },


    //========================================================
    // Drop Overlay
    //========================================================

    dropOverlay:
    {

        position: "absolute",

        top: 0,

        right: 0,

        bottom: 0,

        left: 0,

        zIndex: 20,

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        boxSizing: "border-box",

        pointerEvents: "none"

    },


    dropOverlayEmpty:
    {

        background: "rgba(32,175,232,0.06)"

    },


    dropOverlayPopulated:
    {

        background: "rgba(255,255,255,0.64)",

        backdropFilter: "blur(1px)"

    },


    dropTargetMessage:
    {

        minWidth: 108,

        padding: "12px 18px",

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        justifyContent: "center",

        border: "1px solid rgba(32,175,232,0.42)",

        borderRadius: 5,

        background: "rgba(255,255,255,0.94)",

        color: OMNISCAN_BLUE,

        boxShadow:
            "0 5px 18px rgba(20,35,50,0.10)",

        boxSizing: "border-box"

    },


    dropIcon:
    {

        marginBottom: 4,

        fontSize: 24,

        fontWeight: 300,

        lineHeight: 1

    },


    dropText:
    {

        fontSize: 9,

        fontWeight: 700,

        letterSpacing: 0.3,

        textTransform: "uppercase"

    }

};